import { useParams, Link, useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import {
  ArrowLeft,
  Archive,
  ArchiveRestore,
  Code,
  Zap,
  Settings,
  Users,
  Clock,
  RefreshCw,
  Copy,
  ExternalLink,
  Database,
} from 'lucide-react';
import { formatDateTime } from '../../lib/utils';
import { toast } from 'sonner';

export function SmartCorrectDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: service, isLoading } = useQuery({
    queryKey: ['smart-correct', id],
    queryFn: () =>
      api
        .get('/api/smart-correct/get', { params: { service_instance_id: id } })
        .then((r) => r.data),
    enabled: !!id,
  });

  const { data: codeData } = useQuery({
    queryKey: ['smart-correct-code', id],
    queryFn: () =>
      api
        .post('/api/services/generateCode', { service_instance_id: id })
        .then((r) => r.data),
    enabled: !!id,
  });

  const { data: vendorStats } = useQuery({
    queryKey: ['smart-correct-vendor-stats', id],
    queryFn: () =>
      api
        .get(`/api/smart-correct/${id}/vendors/stats`)
        .then((r) => r.data),
    enabled: !!id,
  });

  const { data: transactions } = useQuery({
    queryKey: ['smart-correct-transactions', id],
    queryFn: () =>
      api
        .get(`/api/smart-correct/${id}/transactions`, {
          params: { limit: 10 },
        })
        .then((r) => r.data),
    enabled: !!id,
  });

  const archiveMutation = useMutation({
    mutationFn: () =>
      api.post('/api/services/archive', { service_instance_id: id }),
    onSuccess: () => {
      toast.success('Service archived');
      queryClient.invalidateQueries({ queryKey: ['smart-correct'] });
      navigate('/smart-correct');
    },
    onError: () => toast.error('Failed to archive service'),
  });

  const restoreMutation = useMutation({
    mutationFn: () =>
      api.post('/api/services/archives/restore', {
        service_instance_id: id,
      }),
    onSuccess: () => {
      toast.success('Service restored');
      queryClient.invalidateQueries({ queryKey: ['smart-correct', id] });
    },
    onError: () => toast.error('Failed to restore service'),
  });

  const syncMutation = useMutation({
    mutationFn: () =>
      api.post(`/api/smart-correct/${id}/sync`),
    onSuccess: () => {
      toast.success('Catalog sync triggered');
      queryClient.invalidateQueries({
        queryKey: ['smart-correct-vendor-stats', id],
      });
    },
    onError: () => toast.error('Failed to trigger sync'),
  });

  if (isLoading) {
    return (
      <div className="text-center py-12 text-gray-500">
        Loading Smart Correct service...
      </div>
    );
  }

  const s = service?.query?.[0] ?? service;
  if (!s) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500 mb-4">Smart Correct service not found</p>
        <Link to="/smart-correct">
          <Button variant="outline">Back to Smart Correct</Button>
        </Link>
      </div>
    );
  }

  const config = s.config?.matching ?? {};
  const catalogConfig = s.config?.catalog ?? {};
  const apiKey = s.service_api_key || codeData?.service_api_key;

  // Generate Vantage script snippet
  const vantageScript = apiKey
    ? `// Auto-generated by Connectfy Portal
const SERVICE_URL = "https://scan.etu.nz/api/v1/suggest-suppliers";
const INSTANCE_ID = "${s.service_instance_id}";
const API_KEY = "${apiKey}";

try {
  let vendorName = Context.Transaction.Documents[i].GetField("Vendor/Name");
  let payload = {
    vendorPayload: {
      name: vendorName,
      address: Context.Transaction.Documents[i].GetField("Vendor/Address"),
      city: Context.Transaction.Documents[i].GetField("Vendor/City"),
      country: Context.Transaction.Documents[i].GetField("Vendor/Country"),
    }
  };

  let response = await fetch(SERVICE_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Authorization": "Basic " + btoa(INSTANCE_ID + ":" + API_KEY),
    },
    body: JSON.stringify(payload),
  });

  let result = await response.json();
  if (result.Update) {
    // Apply confirmed vendor fields
    Context.Transaction.Documents[i].SetField("Vendor/Name", result.name);
    Context.Transaction.Documents[i].SetField("Vendor/ID", result.vendor_id);
  }
} catch (error) {
  console.error("Smart Correct error:", error);
}`
    : '// Generate an API key first, then the script will be available here.';

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <Link to="/smart-correct">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Zap className="h-6 w-6 text-[#cc0404]" />
            {s.service_title}
          </h1>
          <p className="text-gray-500">Smart Correct — Vendor Matching</p>
        </div>
        <Badge variant={s.is_active ? 'success' : 'secondary'}>
          {s.is_active ? 'Active' : s.is_deleted ? 'Archived' : 'Inactive'}
        </Badge>
      </div>

      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="vendors">Vendors</TabsTrigger>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
          <TabsTrigger value="script">Vantage Script</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Zap className="h-5 w-5" /> Service Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Name:</span>{' '}
                  <span className="font-medium ml-2">{s.service_title}</span>
                </div>
                <div>
                  <span className="text-gray-500">Vantage Instance:</span>{' '}
                  <span className="font-medium ml-2">
                    {s.config?.vantage?.app_id || '—'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Data Catalog:</span>{' '}
                  <span className="font-medium ml-2">
                    {s.config?.vantage?.catalog_id || s.config?.catalog?.catalogId || '—'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Created:</span>{' '}
                  <span className="font-medium ml-2">
                    {formatDateTime(s.created)}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Modified:</span>{' '}
                  <span className="font-medium ml-2">
                    {formatDateTime(s.modified)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Settings className="h-5 w-5" /> Matching Configuration
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Match Field:</span>{' '}
                  <span className="font-medium ml-2 capitalize">
                    {config.matchField || 'name'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Fuzzy Threshold:</span>{' '}
                  <span className="font-medium ml-2">
                    {config.fuzzyThreshold ?? 70}%
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Case Sensitive:</span>{' '}
                  <span className="font-medium ml-2">
                    {config.fuzzyCaseSensitive ? 'Yes' : 'No'}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500">Catalog:</span>{' '}
                  <span className="font-medium ml-2 font-mono">
                    {catalogConfig.catalogId || 'EdgeGlobal-vendors'}
                  </span>
                </div>
              </div>
              <div className="mt-4">
                <Link to={`/smart-correct/${id}/config`}>
                  <Button variant="outline" size="sm">
                    <Settings className="h-4 w-4 mr-2" /> Edit Configuration
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>

          {/* API Credentials */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Code className="h-5 w-5" /> API Credentials
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                <div>
                  <span className="text-gray-500">Instance ID:</span>
                  <code className="ml-2 bg-gray-100 px-2 py-0.5 rounded text-xs">
                    {s.service_instance_id}
                  </code>
                </div>
                {apiKey && (
                  <div>
                    <span className="text-gray-500">API Key:</span>
                    <code className="ml-2 bg-gray-100 px-2 py-0.5 rounded text-xs">
                      {apiKey}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-2"
                      onClick={() => {
                        navigator.clipboard.writeText(apiKey);
                        toast.success('API key copied');
                      }}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                )}
                <div>
                  <span className="text-gray-500">Endpoint:</span>
                  <code className="ml-2 bg-gray-100 px-2 py-0.5 rounded text-xs">
                    https://scan.etu.nz/api/v1/suggest-suppliers
                  </code>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Vendor Dashboard */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="h-5 w-5" /> Vendor Dashboard
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 bg-green-50 rounded-lg">
                  <div className="text-2xl font-bold text-green-700">
                    {vendorStats?.confirmed_count ?? '—'}
                  </div>
                  <div className="text-sm text-green-600">Confirmed</div>
                </div>
                <div className="text-center p-4 bg-yellow-50 rounded-lg">
                  <div className="text-2xl font-bold text-yellow-700">
                    {vendorStats?.suggested_count ?? '—'}
                  </div>
                  <div className="text-sm text-yellow-600">Pending Review</div>
                </div>
                <div className="text-center p-4 bg-blue-50 rounded-lg">
                  <div className="text-2xl font-bold text-blue-700">
                    {vendorStats?.last_sync
                      ? formatDateTime(vendorStats.last_sync)
                      : '—'}
                  </div>
                  <div className="text-sm text-blue-600">Last Sync</div>
                </div>
              </div>
              <div className="mt-4 flex gap-3">
                <Link to={`/smart-correct/${id}/vendors`}>
                  <Button variant="outline" size="sm">
                    <Users className="h-4 w-4 mr-2" /> View Vendors
                  </Button>
                </Link>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => syncMutation.mutate()}
                  disabled={syncMutation.isPending}
                >
                  <RefreshCw
                    className={`h-4 w-4 mr-2 ${syncMutation.isPending ? 'animate-spin' : ''}`}
                  />
                  {syncMutation.isPending ? 'Syncing...' : 'Sync Catalog'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Actions */}
          <Separator className="my-6" />
          <div>
            <h2 className="text-lg font-semibold mb-3">Actions</h2>
            <div className="flex gap-3">
              {s.is_deleted ? (
                <Button
                  variant="outline"
                  onClick={() => restoreMutation.mutate()}
                  disabled={restoreMutation.isPending}
                >
                  <ArchiveRestore className="h-4 w-4 mr-2" />
                  {restoreMutation.isPending ? 'Restoring...' : 'Restore'}
                </Button>
              ) : (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline">
                      <Archive className="h-4 w-4 mr-2" /> Archive
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        Archive {s.service_title}?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        This will archive the Smart Correct service. You can
                        restore it later.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => archiveMutation.mutate()}
                        disabled={archiveMutation.isPending}
                      >
                        {archiveMutation.isPending
                          ? 'Archiving...'
                          : 'Archive'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Vendors Tab */}
        <TabsContent value="vendors" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Vendor Management</h2>
            <div className="flex gap-2">
              <Link to={`/smart-correct/${id}/vendors`}>
                <Button variant="outline" size="sm">
                  <Users className="h-4 w-4 mr-2" /> Confirmed Vendors
                </Button>
              </Link>
              <Link to={`/smart-correct/${id}/suggestions`}>
                <Button variant="outline" size="sm">
                  <Clock className="h-4 w-4 mr-2" /> Pending Suggestions
                </Button>
              </Link>
              <Link to={`/smart-correct/${id}/data`}>
                <Button variant="outline" size="sm">
                  <Database className="h-4 w-4 mr-2" /> Data Browser
                </Button>
              </Link>
            </div>
          </div>
          <p className="text-sm text-gray-500">
            Manage confirmed vendors and review pending suggestions. Confirmed
            vendors are used for exact matching. Suggestions need review before
            they become confirmed.
          </p>
        </TabsContent>

        {/* Transactions Tab */}
        <TabsContent value="transactions" className="space-y-4">
          <h2 className="text-lg font-semibold">Recent Transactions</h2>
          {transactions?.query?.data?.length > 0 ? (
            <Card>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-3 text-gray-500">Time</th>
                    <th className="text-left p-3 text-gray-500">Type</th>
                    <th className="text-left p-3 text-gray-500">Status</th>
                    <th className="text-left p-3 text-gray-500">Vendor</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.query.data.map((tx: any) => (
                    <tr key={tx.transaction_id} className="border-b">
                      <td className="p-3">
                        {formatDateTime(tx.start_datetime)}
                      </td>
                      <td className="p-3">{tx.type || '—'}</td>
                      <td className="p-3">
                        <Badge
                          variant={
                            tx.status === 'success' ? 'success' : 'secondary'
                          }
                        >
                          {tx.status || '—'}
                        </Badge>
                      </td>
                      <td className="p-3">{tx.vendor_name || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          ) : (
            <div className="text-center py-8 text-gray-500">
              No transactions yet. Transactions will appear when Vantage starts
              sending data.
            </div>
          )}
        </TabsContent>

        {/* Vantage Script Tab */}
        <TabsContent value="script" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Vantage Script</h2>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                navigator.clipboard.writeText(vantageScript);
                toast.success('Script copied to clipboard');
              }}
            >
              <Copy className="h-4 w-4 mr-2" /> Copy Script
            </Button>
          </div>
          <p className="text-sm text-gray-500">
            Copy this script into your Vantage export configuration. It includes
            your instance ID and API key for authentication.
          </p>
          <pre className="bg-gray-900 text-green-400 rounded-lg p-4 text-xs overflow-x-auto whitespace-pre-wrap">
            {vantageScript}
          </pre>
          <div className="flex gap-2">
            <a
              href="https://docs.connectfy.net/smart-correct/vantage-setup"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="outline" size="sm">
                <ExternalLink className="h-4 w-4 mr-2" /> Setup Guide
              </Button>
            </a>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}